import {
    Box,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material'
import TruncatedText from './TruncatedText'
import { AddRounded, Clear } from '@mui/icons-material'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface SelectInputProps {
    id: string
    label: string
    value: string
    onChange: any
    list: any[]
    inputProps?: {
        id: string
        name: string
    }
    isLegend?: boolean
    required?: boolean
    disabled?: boolean
    hasEmpty?: boolean
    errorMessage?: string
    ui_width?: string
    hasAddNew?: boolean
    onClickAddNew?: any
    onClickRowIcon?: any
    ariaLabel?: string
    title?: string
    allowClear?: boolean
}

const SelectInput = (props: SelectInputProps): ReactElement => {
    const {
        id,
        label,
        value = '',
        onChange,
        list,
        inputProps,
        isLegend = false,
        required,
        hasEmpty = true,
        disabled,
        errorMessage = '',
        ui_width,
        hasAddNew,
        onClickAddNew,
        onClickRowIcon,
        ariaLabel,
        title,
        allowClear = false,
    } = props
    const [open, setOpen] = useState(false)
    const { t } = useTranslation()

    const handleRowIcon = (e: any, item: any) => {
        e.stopPropagation()
        setOpen(false)
        onClickRowIcon(item)
    }

    const handleClear = (e: any) => {
        e.stopPropagation()
        onChange(id, '')
    }

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={!!disabled} size="small">
                {isLegend ? (
                    <InputLabel
                        id={`select-label-id-${id}`}
                        sx={{ opacity: '0.7', fontSize: '14px' }}
                    >
                        {label}
                        {required ? '*' : ''}
                    </InputLabel>
                ) : (
                    <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                        {label}
                        {required ? '*' : ''}
                    </Typography>
                )}
                <Select
                    labelId={isLegend ? `select-label-id-${id}` : ''}
                    id={id}
                    value={value}
                    label={isLegend ? label : ''}
                    size="small"
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    onChange={(e: any) => onChange(id, e.target.value)}
                    sx={{
                        width: '100%',
                        fontSize: '14px',
                        '& div': {
                            opacity:
                                value !== '' && value !== null ? '1' : '0.2',
                        },
                    }}
                    displayEmpty
                    renderValue={
                        value !== '' && value !== null
                            ? undefined
                            : isLegend
                              ? undefined
                              : () => t('selectLabel', { label: label || '' })
                    }
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 250,
                                width: ui_width ? ui_width : '',
                            },
                        },
                    }}
                    error={errorMessage !== ''}
                    aria-label={ariaLabel}
                    title={title}
                    endAdornment={
                        allowClear && value ? (
                            <IconButton
                                sx={{
                                    display: 'block',
                                    position: 'absolute',
                                    right: '25px',
                                }}
                                onClick={handleClear}
                                size="small"
                            >
                                <Clear />
                            </IconButton>
                        ) : null
                    }
                >
                    {hasAddNew && (
                        <MenuItem
                            // value={value}
                            sx={{ background: '#fff !important' }}
                            id={id + 'add-new-option'}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                                onClick={() => onClickAddNew()}
                            >
                                <AddRounded color={'primary'} />
                                <Box
                                    sx={{
                                        marginLeft: '5px',
                                    }}
                                >
                                    <Typography
                                        fontWeight="600"
                                        fontSize="14px"
                                    >
                                        {t('addNew')}
                                    </Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    )}
                    {hasEmpty && (
                        <MenuItem
                            sx={{ fontSize: '14px', opacity: '0.4' }}
                            value=""
                            id={id + 'add-empty-option'}
                        >
                            {t('selectPlaceholder')}
                        </MenuItem>
                    )}

                    {list?.map((item: any, index: number) => {
                        const optionId = inputProps?.id
                            ? item[inputProps.id]
                            : item.id
                        return (
                            <MenuItem
                                id={`${id}-select-option-${index}`}
                                key={optionId}
                                value={optionId}
                                sx={{
                                    fontSize: '14px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '&:hover': {
                                        '& > .row-icon': {
                                            display: 'flex',
                                        },
                                    },
                                    width: '100%',
                                }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width={
                                        onClickRowIcon
                                            ? 'calc(100% - 20px)'
                                            : '100%'
                                    }
                                >
                                    <TruncatedText
                                        text={
                                            inputProps?.name
                                                ? item[inputProps.name]
                                                : item.name
                                        }
                                        sx={{ fontSize: '14px' }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        marginLeft: 'auto',
                                        display: 'none',
                                    }}
                                    className="row-icon"
                                >
                                    {item.hasItemEdit && (
                                        <IconButton
                                            id={`select-item-${index}-edit-icon`}
                                            onClick={(e) =>
                                                handleRowIcon(e, item)
                                            }
                                            aria-haspopup="true"
                                            key="edit"
                                            disabled={false}
                                            sx={{ padding: '0px' }}
                                        >
                                            <img
                                                src="/assets/images/icons/edit.svg"
                                                alt="Edit"
                                            />
                                        </IconButton>
                                    )}
                                </Box>
                            </MenuItem>
                        )
                    })}
                </Select>
                {errorMessage !== '' && (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default SelectInput
